import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { createBooking, getSlotsInRange } from '@/services/integrations/cal';
import { getDefaultLocation, getDistinctCities, CityOption } from '@/services/data/locations';
import { getProductFromPlan } from '@/services/data/planToProduct';
import { getCurrentUser, getUserProfile } from '@/services/data/users';
import { CalSlot } from '@/types/scheduling';
import { logger } from '@/utils/logger';

/**
 * Hook personnalisé pour gérer tout le flow de réservation
 * 
 * Gère :
 * - L'état utilisateur (userId, email, nom)
 * - La sélection de plan et de localisation (ville uniquement, sans réalisateur)
 * - Le chargement des disponibilités (avec cache)
 * - La sélection de date et de créneau
 * - La création de réservation
 * - Les états de chargement et d'erreur
 * 
 * @returns {Object} État et fonctions pour gérer le booking
 * 
 * @example
 * const {
 *   userId,
 *   selectedPlan,
 *   selectPlan,
 *   datesWithAvailability,
 *   selectDate,
 *   createBookingWithValidation
 * } = useBookingFlow();
 */
export function useBookingFlow() {
  // États utilisateur
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [userEmail, setUserEmail] = useState<string>('user@example.com');
  const [userName, setUserName] = useState<string>('John Doe');
  
  // États de sélection
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [shootingDurationMinutes, setShootingDurationMinutes] = useState<number>(180);
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined);
  const [selectedLocationName, setSelectedLocationName] = useState<string | undefined>(undefined);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(undefined);
  const [availableCities, setAvailableCities] = useState<CityOption[]>([]);
  const [defaultLocationId, setDefaultLocationId] = useState<string | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<CalSlot | undefined>(undefined);
  
  // Disponibilités préchargées (6 mois)
  const [availableSlotsByDate, setAvailableSlotsByDate] = useState<Map<string, CalSlot[]>>(new Map());
  const [datesWithAvailability, setDatesWithAvailability] = useState<Set<string>>(new Set());
  const [availableSlots, setAvailableSlots] = useState<CalSlot[]>([]);
  
  // États de chargement
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [isBooking, setIsBooking] = useState<boolean>(false);

  /**
   * Récupère les informations de l'utilisateur connecté et les villes disponibles
   */
  useEffect(() => {
    const fetchUserInfo = async () => {
      const userResult = await getCurrentUser();
      if (userResult.success && userResult.data) {
        const user = userResult.data;
        setUserId(user.id);
        setUserEmail(user.email || 'user@example.com');
        
        const profileResult = await getUserProfile(user.id);
        
        if (profileResult.success && profileResult.data) {
          const profile = profileResult.data;
          const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
          setUserName(fullName || 'User');
        }
      }
      
      // Charger les villes disponibles
      const citiesResult = await getDistinctCities();
      if (citiesResult.success && citiesResult.data) {
        setAvailableCities(citiesResult.data);
        
        // Si une seule ville, la sélectionner automatiquement
        if (citiesResult.data.length === 1) {
          setSelectedCity(citiesResult.data[0].city);
          setSelectedLocationName(citiesResult.data[0].city);
        }
      }
      
      // Récupérer la location par défaut pour les créneaux
      const defaultLoc = await getDefaultLocation();
      if (defaultLoc.success && defaultLoc.data) {
        setDefaultLocationId(defaultLoc.data.id);
        setSelectedLocation(defaultLoc.data.id);
        // N'afficher que la ville, pas l'adresse complète
        if (defaultLoc.data.city) {
          setSelectedCity(defaultLoc.data.city);
          setSelectedLocationName(defaultLoc.data.city);
        }
      }
    };
    fetchUserInfo();
  }, []);

  /**
   * Met à jour le productId quand le plan change
   */
  useEffect(() => {
    const fetchProductId = async () => {
      if (!selectedPlan) {
        setProductId(undefined);
        return;
      }
      
      const product = await getProductFromPlan(selectedPlan);
      
      if (product) {
        setProductId(product.id);
        if (typeof (product as any).shooting_duration_minutes === 'number') {
          setShootingDurationMinutes((product as any).shooting_duration_minutes);
        } else {
          setShootingDurationMinutes(180);
        }
      } else {
        logger.error('No product found for plan', { selectedPlan });
        setProductId(undefined);
        setShootingDurationMinutes(180);
      }
    };
    
    fetchProductId();
  }, [selectedPlan]);

  /**
   * Normalise une date pour l'affichage (fixe le décalage de fuseau)
   */
  const normalizeDateForSelection = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(12, 0, 0, 0);
    return normalized;
  };

  /**
   * Précharge les créneaux pour les 6 prochains mois lors de la sélection du plan
   */
  useEffect(() => {
    const loadAllSlots = async () => {
      if (!selectedPlan || !userId || !productId) return;

      setIsLoadingSlots(true);
      try {
        const newMap = new Map<string, CalSlot[]>();
        const newDates = new Set<string>();
        // Disponibilités à partir de J+15
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + 15);
        startDate.setHours(12, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + 6);
        endDate.setHours(23, 59, 59, 999);

        const slots = await getSlotsInRange(
          startDate,
          endDate,
          userId,
          productId,
          defaultLocationId
        );

        slots.forEach(slot => {
          const dateStr = slot.start.split('T')[0];
          const current = newMap.get(dateStr) || [];
          current.push(slot);
          newMap.set(dateStr, current);
          newDates.add(dateStr);
        });

        setAvailableSlotsByDate(newMap);
        setDatesWithAvailability(newDates);

        // Auto-select first available date if none selected
        if (!selectedDate && newDates.size > 0) {
          const firstDateStr = Array.from(newDates).sort((a, b) => a.localeCompare(b))[0];
          const firstDate = normalizeDateForSelection(new Date(firstDateStr));
          setSelectedDate(firstDate);
          setAvailableSlots(newMap.get(firstDateStr) || []);
        } else if (selectedDate) {
          const dateStr = selectedDate.toISOString().split('T')[0];
          setAvailableSlots(newMap.get(dateStr) || []);
        }
      } catch (error) {
        logger.error('Failed to load slots for 6 months', { error });
        toast.error('Erreur de chargement des créneaux');
      } finally {
        setIsLoadingSlots(false);
      }
    };

    loadAllSlots();
  }, [selectedPlan, userId, productId, defaultLocationId]);

  /**
   * Met à jour les créneaux affichés quand la date change (sans refetch)
   */
  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = selectedDate.toISOString().split('T')[0];
    setAvailableSlots(availableSlotsByDate.get(dateStr) || []);
  }, [selectedDate, availableSlotsByDate]);

  /**
   * Sélectionne un plan
   */
  const selectPlan = (planValue: string) => {
    logger.info('Plan selected', { plan: planValue });
    setSelectedPlan(planValue);
    // Auto-select today's date to trigger slot loading with the new product
    const today = normalizeDateForSelection(new Date());
    setSelectedDate(today);
    setSelectedSlot(undefined);
    setAvailableSlots([]);
  };

  /**
   * Sélectionne une date
   */
  const selectDate = (date: Date | undefined) => {
    const normalized = date ? normalizeDateForSelection(date) : undefined;
    logger.info('Date selected', { date: normalized?.toISOString() });
    setSelectedDate(normalized);
    setSelectedSlot(undefined);
  };

  /**
   * Sélectionne un créneau horaire
   */
  const selectSlot = (slot: CalSlot) => {
    logger.info('Slot selected', { slot: slot.start });
    setSelectedSlot(slot);
  };

  /**
   * Crée une réservation avec validation
   * Gère le multi-hook en modifiant le productId si nécessaire
   */
  const createBookingWithValidation = async (withMultiHook: boolean = false) => {
    if (!selectedSlot || !userId || !productId) {
      logger.warn('Cannot create booking - missing data', { 
        hasSlot: !!selectedSlot, 
        hasUserId: !!userId, 
        hasProductId: !!productId 
      });
      toast.error('Informations manquantes pour la réservation');
      return { success: false };
    }

    setIsBooking(true);
    try {
      // Déterminer le productId final (avec multi-hook si nécessaire)
      let finalProductId = productId;
      
      if (withMultiHook) {
        // Récupérer le plan depuis le productId
        const planValueWithMultiHook = `${selectedPlan}-multihook`;
        const { getProductFromPlan } = await import('@/services/data/planToProduct');
        const multiHookProduct = await getProductFromPlan(planValueWithMultiHook);
        
        if (multiHookProduct) {
          finalProductId = multiHookProduct.id;
          logger.info('Using multi-hook product', { productId: finalProductId });
        } else {
          logger.warn('Multi-hook product not found, using regular product');
        }
      }
      
      logger.info('Creating booking', { 
        slot: selectedSlot.start, 
        withMultiHook,
        userId,
        productId: finalProductId
      });

      const result = await createBooking(
        selectedSlot.start,
        userName,
        userEmail,
        finalProductId,
        userId,
        defaultLocationId
      );

      if (result.success) {
        logger.info('Booking created successfully', { bookingId: result.bookingId });
        
        return { success: true, booking: result };
      } else {
        logger.error('Booking creation failed', { error: result.error });
        toast.error('Erreur lors de la réservation', {
          description: result.error
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Booking creation exception', { error });
      toast.error('Erreur lors de la réservation');
      return { success: false };
    } finally {
      setIsBooking(false);
    }
  };

  /**
   * Réinitialise l'état de réservation
   */
  const resetBooking = () => {
    logger.info('Resetting booking state');
    setSelectedDate(undefined);
    setSelectedSlot(undefined);
    setAvailableSlots([]);
  };

  /**
   * Sélectionne une ville
   */
  const selectCity = (city: string) => {
    logger.info('City selected', { city });
    setSelectedCity(city);
    setSelectedLocationName(city);
    
    // Trouver le premier location_id pour cette ville (pour les créneaux)
    const cityOption = availableCities.find(c => c.city === city);
    if (cityOption && cityOption.locationIds.length > 0) {
      setSelectedLocation(cityOption.locationIds[0]);
      setDefaultLocationId(cityOption.locationIds[0]);
    }
    
    // Reset les créneaux car la localisation a changé
    setSelectedDate(undefined);
    setSelectedSlot(undefined);
    setAvailableSlots([]);
    setAvailableSlotsByDate(new Map());
    setDatesWithAvailability(new Set());
  };

  return {
    // User data
    userId,
    userEmail,
    userName,
    
    // Selection state
    selectedPlan,
    productId,
    shootingDurationMinutes,
    selectedLocation,
    selectedLocationName,
    selectedCity,
    availableCities,
    defaultLocationId,
    selectedDate,
    selectedSlot,
    
    // Slots
    availableSlots,
    datesWithAvailability,
    
    // Loading state
    isLoadingSlots,
    isBooking,
    
    // Actions
    selectPlan,
    selectCity,
    selectDate,
    selectSlot,
    createBookingWithValidation,
    resetBooking,
  };
}
